/** Header: mega menu (details + tab), hover, sticky, drawer mobile a due livelli, ricerca predittiva, barra annunci. */
(() => {
  'use strict';

  class HeaderComponent extends HTMLElement {
    connectedCallback() {
      this.section = this.closest('.shopify-section');
      this.details = [...this.querySelectorAll('details[data-mega]')];
      this.hover = this.dataset.hover === 'true' && window.matchMedia('(hover: hover)').matches;
      this.setupMega();
      this.setupTabs();
      this.setupDrawer();
      this.setupSearch();
      this.setupScroll();
      document.addEventListener('theme:cart:updated', (e) => this.updateCartCount(e.detail));
    }

    /* ---- mega menu ---- */
    setupMega() {
      this.details.forEach((d) => {
        const summary = d.querySelector('summary');
        d.addEventListener('toggle', () => {
          summary.setAttribute('aria-expanded', String(d.open));
          if (d.open) {
            this.details.forEach((o) => o !== d && o.removeAttribute('open'));
            this.classList.add('is-open');
          } else if (!this.details.some((o) => o.open)) {
            this.classList.remove('is-open');
          }
        });
        // da tastiera: uscendo con Tab dal menu aperto, si chiude
        d.addEventListener('focusout', (e) => {
          if (d.open && e.relatedTarget && !d.contains(e.relatedTarget)) d.removeAttribute('open');
        });
        if (this.hover) {
          const li = d.parentElement;
          let t;
          li.addEventListener('mouseenter', () => {
            clearTimeout(t);
            t = setTimeout(() => d.setAttribute('open', ''), 100);
          });
          li.addEventListener('mouseleave', () => {
            clearTimeout(t);
            t = setTimeout(() => d.removeAttribute('open'), 200);
          });
          // con hover attivo, il click sul summary porta alla pagina della voce
          summary.addEventListener('click', (e) => {
            const url = summary.dataset.url;
            if (d.open && url && url !== '#') {
              e.preventDefault();
              window.location.href = url;
            }
          });
        }
      });
      document.addEventListener('click', (e) => {
        if (!this.contains(e.target)) this.closeAll();
      });
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape') {
          const open = this.details.find((d) => d.open);
          if (open) {
            open.removeAttribute('open');
            open.querySelector('summary').focus();
          }
        }
      });
    }

    closeAll() {
      this.details.forEach((d) => d.removeAttribute('open'));
    }

    setupTabs() {
      this.querySelectorAll('.mega__tabs').forEach((list) => {
        const tabs = [...list.querySelectorAll('[data-mega-tab]')];
        const activate = (tab) => {
          tabs.forEach((t) => {
            const on = t === tab;
            t.setAttribute('aria-selected', String(on));
            t.tabIndex = on ? 0 : -1;
            const panel = document.getElementById(t.getAttribute('aria-controls'));
            if (panel) panel.hidden = !on;
          });
        };
        tabs.forEach((tab, i) => {
          tab.addEventListener('click', () => activate(tab));
          tab.addEventListener('keydown', (e) => {
            const next = e.key === 'ArrowDown' ? i + 1 : e.key === 'ArrowUp' ? i - 1 : null;
            if (next === null) return;
            e.preventDefault();
            const target = tabs[(next + tabs.length) % tabs.length];
            activate(target);
            target.focus();
          });
        });
      });
    }

    /* ---- drawer mobile ---- */
    setupDrawer() {
      this.drawer = this.querySelector('#MenuDrawer');
      const opener = this.querySelector('[data-drawer-open]');
      if (!this.drawer || !opener) return;
      opener.addEventListener('click', () => {
        this.drawer.showModal();
        opener.setAttribute('aria-expanded', 'true');
        this.releaseFocus = window.themeTrapFocus(this.drawer, this.drawer.querySelector('[data-drawer-close]'));
      });
      const close = () => this.drawer.close();
      this.drawer.querySelector('[data-drawer-close]').addEventListener('click', close);
      this.drawer.addEventListener('click', (e) => {
        if (e.target === this.drawer) close();
      });
      this.drawer.addEventListener('close', () => {
        opener.setAttribute('aria-expanded', 'false');
        this.releaseFocus?.();
        this.drawer.querySelectorAll('.drawer__sub.is-open').forEach((sub) => {
          sub.classList.remove('is-open');
          sub.hidden = true;
        });
        opener.focus();
      });
      // secondo livello a pannello
      this.drawer.querySelectorAll('[data-drawer-sub]').forEach((btn) => {
        const sub = this.drawer.querySelector('#' + btn.dataset.drawerSub);
        if (!sub) return;
        btn.addEventListener('click', () => {
          sub.hidden = false;
          requestAnimationFrame(() => sub.classList.add('is-open'));
          btn.setAttribute('aria-expanded', 'true');
          sub.querySelector('[data-drawer-back]')?.focus();
        });
        sub.querySelector('[data-drawer-back]')?.addEventListener('click', () => {
          sub.classList.remove('is-open');
          btn.setAttribute('aria-expanded', 'false');
          setTimeout(() => (sub.hidden = true), 500);
          btn.focus();
        });
      });
    }

    /* ---- ricerca ---- */
    setupSearch() {
      this.search = this.querySelector('#SearchDrawer');
      const openers = [...this.querySelectorAll('[data-search-open]')];
      if (!this.search || !openers.length) return;
      const input = this.search.querySelector('input[type="search"]');
      let lastOpener = openers[0];
      openers.forEach((opener) =>
        opener.addEventListener('click', () => {
          lastOpener = opener;
          this.closeAll();
          this.search.showModal();
          openers.forEach((o) => o.setAttribute('aria-expanded', 'true'));
          this.releaseSearchFocus = window.themeTrapFocus(this.search, input);
        })
      );
      this.search.querySelector('[data-search-close]').addEventListener('click', () => this.search.close());
      this.search.addEventListener('click', (e) => {
        if (e.target === this.search) this.search.close();
      });
      this.search.addEventListener('close', () => {
        openers.forEach((o) => o.setAttribute('aria-expanded', 'false'));
        this.releaseSearchFocus?.();
        lastOpener.focus();
      });
    }

    /* ---- scroll: sticky e trasparenza ---- */
    setupScroll() {
      const mode = this.dataset.sticky;
      const transparent = this.classList.contains('header--transparent');
      if (transparent && this.section) {
        const bar = this.querySelector('.header__bar');
        const misura = () => this.section.style.setProperty('--header-overlap', `${bar.offsetHeight}px`);
        misura();
        if (window.ResizeObserver) new ResizeObserver(misura).observe(bar);
      }
      if (mode === 'none' && this.section) this.section.style.position = 'relative';
      if (mode === 'none' && !transparent) return;
      let last = window.scrollY;
      const onScroll = () => {
        const y = window.scrollY;
        if (transparent) this.classList.toggle('is-scrolled', y > 10);
        if (mode === 'scroll-up' && this.section) {
          const goingDown = y > last && y > this.offsetHeight * 1.5;
          this.section.classList.toggle('is-hidden', goingDown && !this.classList.contains('is-open'));
        }
        last = y;
      };
      window.addEventListener('scroll', onScroll, { passive: true });
      onScroll();
    }

    updateCartCount(detail) {
      const count = detail?.item_count;
      if (typeof count !== 'number') return;
      this.querySelectorAll('[data-cart-count]').forEach((el) => {
        el.textContent = count;
        el.dataset.cartCount = count;
      });
    }
  }
  customElements.define('header-component', HeaderComponent);

  /** Ricerca predittiva: interroga la Section Rendering API della sezione `predictive-search`. */
  class PredictiveSearch extends HTMLElement {
    connectedCallback() {
      this.input = this.querySelector('input[type="search"]');
      this.results = this.querySelector('[data-predictive-results]');
      this.status = this.querySelector('[data-predictive-status]');
      this.controller = null;
      this.input.addEventListener('input', window.themeDebounce(() => this.search(), 250));
    }

    async search() {
      const q = this.input.value.trim();
      if (q.length < 2) {
        this.results.innerHTML = '';
        this.input.setAttribute('aria-expanded', 'false');
        return;
      }
      this.controller?.abort();
      this.controller = new AbortController();
      this.status.classList.remove('visually-hidden');
      try {
        const url = `${this.dataset.url}?q=${encodeURIComponent(q)}&resources[type]=product,collection,page,article,query&resources[limit]=6&resources[limit_scope]=each&section_id=predictive-search`;
        const res = await fetch(url, { signal: this.controller.signal });
        if (!res.ok) throw new Error(res.status);
        const html = await res.text();
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const inner = doc.querySelector('#shopify-section-predictive-search');
        this.results.innerHTML = inner ? inner.innerHTML : '';
        this.input.setAttribute('aria-expanded', 'true');
      } catch (e) {
        if (e.name !== 'AbortError') this.results.innerHTML = '';
      } finally {
        this.status.classList.add('visually-hidden');
      }
    }
  }
  customElements.define('predictive-search', PredictiveSearch);

  /** Barra annunci: messaggi a rotazione con scorrimento verticale. */
  class AnnouncementBar extends HTMLElement {
    connectedCallback() {
      this.items = [...this.querySelectorAll('.topbar__msg')];
      if (this.items.length < 2) return;
      this.index = 0;
      if (this.dataset.rotate === 'true' && !window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        const ms = (parseInt(this.dataset.interval, 10) || 5) * 1000;
        this.timer = setInterval(() => this.go(1), ms);
        this.addEventListener('mouseenter', () => clearInterval(this.timer));
        this.addEventListener('mouseleave', () => (this.timer = setInterval(() => this.go(1), ms)));
      }
    }
    go(step) {
      const current = this.items[this.index];
      current.classList.remove('is-active');
      current.classList.add('is-leaving');
      setTimeout(() => current.classList.remove('is-leaving'), 700);
      this.index = (this.index + step + this.items.length) % this.items.length;
      this.items[this.index].classList.add('is-active');
    }
  }
  customElements.define('announcement-bar', AnnouncementBar);
})();
